/**
 * Word numbering.xml parser + counter logic.
 * Ported from backend/app/rag/parser/docx_numbering.py
 */

import {
  collectParagraphElements,
  firstChildNS,
  isParseError,
  paragraphPlainText,
  parseXml,
  wAttr,
  W_NS,
} from "./xmlUtils";

export interface LevelDef {
  numFmt: string;
  start: number;
  lvlText: string;
  lvlRestartIlvl: number | null;
}

export interface NumberedParagraphMeta {
  /** Index among all w:p elements in document order */
  index: number;
  numId: number | null;
  ilvl: number | null;
  numFmt: string | null;
  computedNumber: string | null;
  rawText: string;
}

export interface NumberingDefinitions {
  numToAbstract: Map<number, number>;
  abstractToLevels: Map<number, Map<number, LevelDef>>;
  numOverrides: Map<number, Map<number, LevelOverride>>;
}

interface LevelOverride {
  startOverride?: number;
  lvlReplacement?: Element;
}

const ALREADY_NUMBERED_RE = /^\d+[A-Z]?.\s/;

function intToRoman(n: number, upper: boolean): string {
  if (n <= 0) return "0";
  const vals: [number, string][] = [
    [1000, "M"],
    [900, "CM"],
    [500, "D"],
    [400, "CD"],
    [100, "C"],
    [90, "XC"],
    [50, "L"],
    [40, "XL"],
    [10, "X"],
    [9, "IX"],
    [5, "V"],
    [4, "IV"],
    [1, "I"],
  ];
  let x = n;
  const out: string[] = [];
  for (const [v, sym] of vals) {
    while (x >= v) {
      out.push(sym);
      x -= v;
    }
  }
  const s = out.join("");
  return upper ? s : s.toLowerCase();
}

function intToLetter(n: number, upper: boolean): string {
  if (n <= 0) return "";
  const chars: string[] = [];
  let x = n;
  while (x > 0) {
    x--;
    chars.push(String.fromCharCode((upper ? 65 : 97) + (x % 26)));
    x = Math.floor(x / 26);
  }
  return chars.reverse().join("");
}

function formatCounter(numFmt: string, value: number): string | null {
  const fmt = (numFmt || "decimal").toLowerCase();
  if (["bullet", "none", "ordinal", "cardinaltext", "ordtext"].includes(fmt)) {
    return null;
  }
  if (fmt === "decimal" || fmt === "arabic" || fmt === "arabic1") {
    return String(value);
  }
  if (fmt === "lowerletter") return intToLetter(value, false);
  if (fmt === "upperletter") return intToLetter(value, true);
  if (fmt === "lowerroman") return intToRoman(value, false);
  if (fmt === "upperroman") return intToRoman(value, true);
  return String(value);
}

function applyLvlText(template: string, ilvlToPiece: Map<number, string>): string {
  if (!template) return `${ilvlToPiece.get(0) ?? ""}.`;
  let out = template;
  ilvlToPiece.forEach((piece, il) => {
    out = out.replace(`%${il + 1}`, piece);
  });
  return out;
}

function readLevelDef(lvlEl: Element): LevelDef | null {
  const ilS = wAttr(lvlEl, "ilvl");
  if (!ilS || !/^\d+$/.test(ilS)) return null;

  const startEl = firstChildNS(lvlEl, "start");
  const startVal = startEl ? wAttr(startEl, "val") : null;
  const start =
    startVal && /^\d+$/.test(startVal) ? parseInt(startVal, 10) : 1;

  const fmtEl = firstChildNS(lvlEl, "numFmt");
  const numFmt = fmtEl ? wAttr(fmtEl, "val") ?? "decimal" : "decimal";

  const txtEl = firstChildNS(lvlEl, "lvlText");
  const lvlText = txtEl ? wAttr(txtEl, "val") ?? "%1." : "%1.";

  const restartEl = firstChildNS(lvlEl, "lvlRestart");
  const restartVal = restartEl ? wAttr(restartEl, "val") : null;
  const lvlRestartIlvl =
    restartVal != null && /^\d+$/.test(restartVal)
      ? parseInt(restartVal, 10)
      : null;

  return { numFmt, start, lvlText, lvlRestartIlvl };
}

function readLevelDefFromElement(lvlEl: Element, base: LevelDef): LevelDef {
  const startEl = firstChildNS(lvlEl, "start");
  const startVal = startEl ? wAttr(startEl, "val") : null;
  const start =
    startVal && /^\d+$/.test(startVal) ? parseInt(startVal, 10) : base.start;

  const fmtEl = firstChildNS(lvlEl, "numFmt");
  const numFmt = fmtEl ? wAttr(fmtEl, "val") ?? base.numFmt : base.numFmt;

  const txtEl = firstChildNS(lvlEl, "lvlText");
  const lvlText = txtEl ? wAttr(txtEl, "val") ?? base.lvlText : base.lvlText;

  const restartEl = firstChildNS(lvlEl, "lvlRestart");
  const restartVal = restartEl ? wAttr(restartEl, "val") : null;
  const lvlRestartIlvl =
    restartVal != null && /^\d+$/.test(restartVal)
      ? parseInt(restartVal, 10)
      : base.lvlRestartIlvl;

  return { numFmt, start, lvlText, lvlRestartIlvl };
}

export function parseNumberingDefinitions(xml: string): NumberingDefinitions {
  const numToAbstract = new Map<number, number>();
  const abstractToLevels = new Map<number, Map<number, LevelDef>>();
  const numOverrides = new Map<number, Map<number, LevelOverride>>();

  const doc = parseXml(xml);
  if (isParseError(doc)) {
    return { numToAbstract, abstractToLevels, numOverrides };
  }

  const abstractNums = doc.getElementsByTagNameNS(W_NS, "abstractNum");
  for (let i = 0; i < abstractNums.length; i++) {
    const absEl = abstractNums[i];
    const aidS = wAttr(absEl, "abstractNumId");
    if (!aidS || !/^\d+$/.test(aidS)) continue;
    const aid = parseInt(aidS, 10);
    const levels = new Map<number, LevelDef>();
    const lvlEls = absEl.getElementsByTagNameNS(W_NS, "lvl");
    for (let j = 0; j < lvlEls.length; j++) {
      const def = readLevelDef(lvlEls[j]);
      if (!def) continue;
      const ilS = wAttr(lvlEls[j], "ilvl");
      if (ilS && /^\d+$/.test(ilS)) {
        levels.set(parseInt(ilS, 10), def);
      }
    }
    abstractToLevels.set(aid, levels);
  }

  const nums = doc.getElementsByTagNameNS(W_NS, "num");
  for (let i = 0; i < nums.length; i++) {
    const numEl = nums[i];
    const nidS = wAttr(numEl, "numId");
    if (!nidS || !/^\d+$/.test(nidS)) continue;
    const nid = parseInt(nidS, 10);

    const absRef = firstChildNS(numEl, "abstractNumId");
    const aval = absRef ? wAttr(absRef, "val") : null;
    if (!aval || !/^\d+$/.test(aval)) continue;
    numToAbstract.set(nid, parseInt(aval, 10));

    const overrides = new Map<number, LevelOverride>();
    const ovEls = numEl.getElementsByTagNameNS(W_NS, "lvlOverride");
    for (let j = 0; j < ovEls.length; j++) {
      const ov = ovEls[j];
      const ilS = wAttr(ov, "ilvl");
      if (!ilS || !/^\d+$/.test(ilS)) continue;
      const il = parseInt(ilS, 10);
      const entry: LevelOverride = {};
      const so = firstChildNS(ov, "startOverride");
      const soVal = so ? wAttr(so, "val") : null;
      if (soVal && /^\d+$/.test(soVal)) {
        entry.startOverride = parseInt(soVal, 10);
      }
      const innerLvl = firstChildNS(ov, "lvl");
      if (innerLvl) entry.lvlReplacement = innerLvl;
      overrides.set(il, entry);
    }
    if (overrides.size) numOverrides.set(nid, overrides);
  }

  return { numToAbstract, abstractToLevels, numOverrides };
}

function mergedLevelDef(
  abstractId: number,
  ilvl: number,
  numId: number,
  defs: NumberingDefinitions,
): LevelDef | null {
  const base = defs.abstractToLevels.get(abstractId)?.get(ilvl);
  if (!base) return null;
  const ov = defs.numOverrides.get(numId)?.get(ilvl);
  if (!ov) return base;

  let start = base.start;
  if (ov.startOverride != null) start = ov.startOverride;
  if (ov.lvlReplacement) {
    return readLevelDefFromElement(ov.lvlReplacement, { ...base, start });
  }
  return { ...base, start };
}

function snapshotFormatted(
  numId: number,
  abstractId: number,
  counters: Map<string, number>,
  defs: NumberingDefinitions,
): Map<number, string> {
  const ilvlToPiece = new Map<number, string>();
  for (let il = 0; il < 9; il++) {
    const key = counterKey(numId, il);
    if (!counters.has(key)) continue;
    const ld = mergedLevelDef(abstractId, il, numId, defs);
    if (!ld) continue;
    if (["bullet", "none"].includes((ld.numFmt || "").toLowerCase())) continue;
    const piece = formatCounter(ld.numFmt, counters.get(key)!);
    if (piece != null) ilvlToPiece.set(il, piece);
  }
  return ilvlToPiece;
}

function counterKey(numId: number, ilvl: number): string {
  return `${numId}:${ilvl}`;
}

function readNumPr(pEl: Element): { numId: number | null; ilvl: number | null } {
  const pPr = firstChildNS(pEl, "pPr");
  const numPr = pPr ? firstChildNS(pPr, "numPr") : null;
  if (!numPr) return { numId: null, ilvl: null };

  const nidEl = firstChildNS(numPr, "numId");
  const ilEl = firstChildNS(numPr, "ilvl");
  let numId: number | null = null;
  let ilvl: number | null = null;

  const nidVal = nidEl ? wAttr(nidEl, "val") : null;
  if (nidVal && /^\d+$/.test(nidVal)) {
    const v = parseInt(nidVal, 10);
    if (v !== 0) numId = v;
  }
  const ilVal = ilEl ? wAttr(ilEl, "val") : null;
  if (ilVal && /^\d+$/.test(ilVal)) ilvl = parseInt(ilVal, 10);

  return { numId, ilvl };
}

function stripForAlreadyNumberedCheck(s: string): string {
  return s.replace(/^[* \t]+/, "");
}

export function isAlreadyNumbered(rawText: string): boolean {
  return ALREADY_NUMBERED_RE.test(stripForAlreadyNumberedCheck(rawText));
}

export function extractNumberedParagraphMeta(
  documentXml: string,
  numberingXml: string | null,
): NumberedParagraphMeta[] {
  const defs = numberingXml
    ? parseNumberingDefinitions(numberingXml)
    : {
        numToAbstract: new Map(),
        abstractToLevels: new Map(),
        numOverrides: new Map(),
      };

  const doc = parseXml(documentXml);
  if (isParseError(doc)) return [];

  const counters = new Map<string, number>();
  const paragraphs = collectParagraphElements(doc);

  return paragraphs.map((pEl, index) => {
    const rawText = paragraphPlainText(pEl).trim();
    const { numId, ilvl } = readNumPr(pEl);
    let computedNumber: string | null = null;
    let numFmt: string | null = null;

    if (numId != null && ilvl != null) {
      const abstractId = defs.numToAbstract.get(numId);
      if (abstractId != null) {
        const lvlDef = mergedLevelDef(abstractId, ilvl, numId, defs);
        if (lvlDef) {
          numFmt = lvlDef.numFmt;
          for (const key of [...counters.keys()]) {
            const [kNumId, kIlvl] = key.split(":").map(Number);
            if (kNumId === numId && kIlvl > ilvl) counters.delete(key);
          }

          const fmtL = (lvlDef.numFmt || "").toLowerCase();
          if (!["bullet", "none"].includes(fmtL)) {
            const key = counterKey(numId, ilvl);
            if (!counters.has(key)) {
              counters.set(key, lvlDef.start);
            } else {
              counters.set(key, counters.get(key)! + 1);
            }
            const ilvlToPiece = snapshotFormatted(
              numId,
              abstractId,
              counters,
              defs,
            );
            computedNumber = applyLvlText(lvlDef.lvlText, ilvlToPiece);
          }
        }
      }
    }

    return {
      index,
      numId,
      ilvl,
      numFmt,
      computedNumber,
      rawText,
    };
  });
}

export function numFmtToListStyleType(numFmt: string | null): string | null {
  if (!numFmt) return null;
  switch (numFmt.toLowerCase()) {
    case "decimal":
    case "arabic":
    case "arabic1":
      return "decimal";
    case "lowerletter":
      return "lower-alpha";
    case "upperletter":
      return "upper-alpha";
    case "lowerroman":
      return "lower-roman";
    case "upperroman":
      return "upper-roman";
    default:
      return null;
  }
}
